import { ReactElement, useMemo } from 'react';

import { PractitionerView } from '@dreryk/egabinet-personnel-frontend-api';

import { BindingCreator, createTranslatedText, practitionerNameFormatter, useTranslate } from '@dreryk/react-common';
import { FilterPanel, Multiselect } from '@dreryk/react-components';

import {
   GroupSessionsFilterPanelFormBean,
   GroupSessionsFilterPanelSearchParam,
   GroupSessionsFilterPanelStorageKey,
} from '../../GroupSessionsFilterPanel.types';
import { translationKeys } from '../../../translations';

import {
   createPractitionersCodec,
   createPractitionersTriggerLabelExtractor,
   practitionersDeselectorsPropsMapper,
} from './PractitionersController.utils';
import { StyledPractitionersMultiselect } from './PractitionersMultiselect.styled';

type PractitionersControllerProps = {
   registerController: BindingCreator<GroupSessionsFilterPanelFormBean, 'practitioners'>;
   practitioners: PractitionerView[];
};

export const PractitionersController = (props: PractitionersControllerProps): ReactElement => {
   const translate = useTranslate();
   const codec = useMemo(() => createPractitionersCodec(props.practitioners), [props.practitioners]);
   return (
      <FilterPanel.Controller<GroupSessionsFilterPanelFormBean, 'practitioners'>
         name={'practitioners'}
         searchParam={GroupSessionsFilterPanelSearchParam.PRACTITIONER}
         storageKey={GroupSessionsFilterPanelStorageKey.PRACTITIONER}
         codec={codec}
         chipsLabel={translationKeys.instructor}
         getDeselectorsProps={practitionersDeselectorsPropsMapper}
      >
         {({ applyFilter }) => (
            <StyledPractitionersMultiselect<PractitionerView>
               testId={'practitioners'}
               triggerLabelExtractor={createPractitionersTriggerLabelExtractor(props.practitioners.length, translate)}
               controller={props.registerController('practitioners')}
               onHide={applyFilter}
               label={translationKeys.instructor}
               placeholder={translationKeys.chooseInstructors}
               itemKeyGenerator={'id'}
               compact
            >
               <>
                  <Multiselect.SelectAll testId={'selectAll'} items={props.practitioners} />
                  <Multiselect.List<PractitionerView> items={props.practitioners} keyExtractor={'id'}>
                     {(item) => (
                        <Multiselect.Item
                           item={item}
                           label={createTranslatedText(practitionerNameFormatter(item))}
                           testId={item.id}
                        />
                     )}
                  </Multiselect.List>
               </>
            </StyledPractitionersMultiselect>
         )}
      </FilterPanel.Controller>
   );
};
